import { Button } from '@/components/shadcn/button'
import { Badge } from '@/components/shadcn/badge'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/shadcn/sheet'
import { Textarea } from '@/components/shadcn/textarea'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { ChatMultiple24Regular } from '@fluentui/react-icons'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AgentForm } from '../Forms/AgentForm'
import { useCurrentApplication } from '../useCurrentApplication'
import { AppCard } from './AppCard/AppCard'
import { AgentCardActionsMenu } from './AppCard/AgentCardActionsMenu'
import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { EmptyState } from '@/components/ui/EmptyState'

type MessagesAgentExistingAgent = RestSchemas['Messages.Agent.ExistingAgent']

export const AppAgentsSection = () => {
  const { applicationId, appName } = useCurrentApplication()
  const { getAgentApiGetAgents } = useRestClient()

  const invalidateAgents = useCallback(() => {
    getAgentApiGetAgents.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getAgentApiGetAgents])

  return (
    <SectionContainer
      title="Agents"
      action={
        <AgentForm
          sheetTitle={`${appName} > Add agent`}
          sheetDescription="Add a new agent to your application."
          sheetTriggerTooltip="Add agent"
          onSubmit={invalidateAgents}
          sheetTrigger={
            <Button variant="ghost" size="icon">
              <IconPlusOutline />
              <span className="sr-only">Add Agent</span>
            </Button>
          }
        />
      }
    >
      <QueryErrorBoundary errorMessage="Failed to fetch agents">
        <Suspense fallback={<LoadingComponent text="Loading agents..." />}>
          <AppAgentsList />
        </Suspense>
      </QueryErrorBoundary>
    </SectionContainer>
  )
}

function AppAgentsList() {
  const { applicationId, appName } = useCurrentApplication()
  const { getAgentApiGetAgents, getProviderRegistrationApiListProviders, getToolRegistrationApiListTools } =
    useRestClient()

  const { data: agents } = getAgentApiGetAgents.useSuspenseQuery({
    path: { applicationId },
  })
  const { data: providers } = getProviderRegistrationApiListProviders.useSuspenseQuery(undefined)
  const { data: tools } = getToolRegistrationApiListTools.useSuspenseQuery(undefined)

  const providerByKey = useMemo(
    () => new Map((providers ?? []).map((provider) => [provider.key, provider])),
    [providers]
  )
  const toolByFunctionName = useMemo(() => {
    const map = new Map<string, { name: string; functionName?: string | null }>()
    for (const tool of tools ?? []) {
      if (tool.functionName) {
        map.set(tool.functionName, tool)
      }
    }
    return map
  }, [tools])

  const invalidateAgents = useCallback(() => {
    getAgentApiGetAgents.invalidateQueries({ parameters: { path: { applicationId } } })
  }, [applicationId, getAgentApiGetAgents])

  return (
    <>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-1">
        {agents?.map((agent) => (
          <AppCard
            key={agent.id}
            icon={<ChatMultiple24Regular fontSize={34} />}
            name={agent.name || 'no name'}
            description={agent.description || 'no description'}
            cardDetails={
              <AgentCardDetails
                agent={agent}
                onUpdated={invalidateAgents}
                providerByKey={providerByKey}
                toolByFunctionName={toolByFunctionName}
              />
            }
            cardMenu={<AgentCardActionsMenu agent={agent} />}
          />
        ))}
      </div>
      {(!agents || agents.length === 0) && (
        <EmptyState
          title="No agents found."
          description="Click the + button above to add your first agent."
          actions={
            <AgentForm
              sheetTitle={`${appName} > Add agent`}
              sheetDescription="Add a new agent to your application."
              sheetTriggerTooltip="Add agent"
              onSubmit={invalidateAgents}
              sheetTrigger={
                <Button variant="outline" size="sm">
                  <IconPlusOutline />
                  <span className="ml-2">Add Agent</span>
                </Button>
              }
            />
          }
        />
      )}
    </>
  )
}

function AgentCardDetails({
  agent,
  onUpdated,
  providerByKey,
  toolByFunctionName,
}: {
  agent: MessagesAgentExistingAgent
  onUpdated: () => void
  providerByKey: Map<string, { key: string; name: string }>
  toolByFunctionName: Map<string, { name: string; functionName?: string | null }>
}) {
  const { applicationId } = useCurrentApplication()
  const { postAgentApiUpdateAgent } = useRestClient()
  const [promptOpen, setPromptOpen] = useState(false)
  const [systemPromptDraft, setSystemPromptDraft] = useState(agent.systemPrompt || '')

  const resolvedProviderName = providerByKey.get(agent.providerKey)?.name ?? agent.providerKey ?? 'n/a'

  const toolLabels = (agent.tools ?? []).map(
    (functionName) => toolByFunctionName.get(functionName)?.name ?? functionName
  )
  const visibleTools = toolLabels.slice(0, 3)
  const hiddenToolsCount = Math.max(toolLabels.length - visibleTools.length, 0)

  useEffect(() => {
    setSystemPromptDraft(agent.systemPrompt || '')
  }, [agent.systemPrompt])

  const promptPreview = useMemo(() => {
    if (!agent.systemPrompt) return 'No system prompt. Click to add one.'
    return agent.systemPrompt
  }, [agent.systemPrompt])

  const { mutate: updateAgentPrompt, isPending: isUpdatingPrompt } = postAgentApiUpdateAgent.useMutation(undefined, {
    onSuccess: () => {
      onUpdated()
      setPromptOpen(false)
      toast.success('System prompt updated')
    },
    onError: () => {
      toast.error('Error updating system prompt')
    },
  })

  const handlePromptSave = useCallback(() => {
    if (systemPromptDraft === (agent.systemPrompt || '')) {
      setPromptOpen(false)
      return
    }

    if (!agent.providerKey) {
      toast.error('Cannot update prompt: provider could not be resolved.')
      return
    }

    updateAgentPrompt({
      body: {
        id: agent.id,
        key: agent.key || '',
        name: agent.name || '',
        description: agent.description || '',
        providerKey: agent.providerKey,
        model: agent.model || '',
        temperature: agent.temperature ?? 0,
        maxTokens: agent.maxTokens ?? 1,
        tools: agent.tools ?? [],
        systemPrompt: systemPromptDraft,
        locked: agent.locked ?? false,
        contextManagementEnabled: agent.contextManagementEnabled ?? false,
        contextWindow: agent.contextWindow ?? 0,
        compactorTokenThresholdPct: agent.compactorTokenThresholdPct ?? 0.7,
        compactorMessageThreshold: agent.compactorMessageThreshold ?? 40,
        compactorKeepRecent: agent.compactorKeepRecent ?? 20,
        compactorSummaryMaxTokens: agent.compactorSummaryMaxTokens ?? 800,
        compactorModel: agent.compactorModel ?? '',
      },
      path: { applicationId },
    })
  }, [agent, applicationId, systemPromptDraft, updateAgentPrompt])

  const badgeClass = 'text-foreground'
  const keyClass = 'font-light'
  const valueClass = 'ml-1'

  return (
    <div className="space-y-4">
      <Sheet open={promptOpen} onOpenChange={setPromptOpen}>
        <SheetTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="h-auto w-full justify-between gap-2 overflow-hidden rounded-md border bg-muted/30 px-2 py-2 text-left shadow-none hover:bg-muted/50"
          >
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">System prompt</div>
              <p className="mt-1 truncate text-xs text-muted-foreground">{promptPreview}</p>
            </div>
            <span className="flex shrink-0 items-center gap-1 text-[10px] text-muted-foreground">
              <IconEditOutline className="size-3" />
            </span>
          </Button>
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-3xl">
          <SheetHeader>
            <SheetTitle>Edit system prompt</SheetTitle>
            <SheetDescription>Update the system prompt for {agent.name || 'this agent'}.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            <Textarea
              value={systemPromptDraft}
              onChange={(event) => setSystemPromptDraft(event.target.value)}
              className="min-h-[240px] text-xs"
              placeholder="Enter a system prompt..."
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setSystemPromptDraft(agent.systemPrompt || '')
                  setPromptOpen(false)
                }}
                disabled={isUpdatingPrompt}
              >
                Cancel
              </Button>
              <Button type="button" size="sm" onClick={handlePromptSave} disabled={isUpdatingPrompt}>
                Save
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <div>
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Settings</div>
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className={badgeClass}>
            <span className={keyClass}>Provider:</span> <span className={valueClass}>{resolvedProviderName}</span>
          </Badge>
          <Badge variant="secondary" className={badgeClass}>
            <span className={keyClass}>Model:</span> <span className={valueClass}>{agent.model || 'n/a'}</span>
          </Badge>
          <Badge variant="secondary" className={badgeClass}>
            <span className={keyClass}>Temp:</span> <span className={valueClass}>{agent.temperature ?? 'n/a'}</span>
          </Badge>
          <Badge variant="secondary" className={badgeClass}>
            <span className={keyClass}>Max tokens:</span> <span className={valueClass}>{agent.maxTokens ?? 'n/a'}</span>
          </Badge>
        </div>
      </div>
      <div className="flex gap-1.5">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Context management:</div>
        <Badge variant={agent.contextManagementEnabled ? 'success' : 'outline'}>
          {agent.contextManagementEnabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-foreground">Tools:</span>
        {visibleTools.length > 0 ? (
          visibleTools.map((tool, index) => (
            <Badge key={`${tool}-${index}`} variant="outline">
              {tool}
            </Badge>
          ))
        ) : (
          <span className="text-xs text-muted-foreground">None</span>
        )}
        {hiddenToolsCount > 0 ? <Badge variant="outline">+{hiddenToolsCount} more</Badge> : null}
      </div>
    </div>
  )
}
